import PropTypes from 'prop-types';
import React from 'react';

import fetchProviderRoutes from '../../resources/clients/provider/fetchProviderRoutes';
import extractQuote from '../../resources/clients/quotes/extractQuote';

const transformProviderResponse = (response) => ({
  routes: (response.routes || []).map((r) => ({
    sourceCurrency: r.sourceCurrency,
    targetCurrency: r.targetCurrency,
    ...extractQuote(r.quotes, r.targetCurrency),
  })),
});

/**
 * To optimize the data merging algorithm, the comparedProvider data
 * must be reorganised into a map that indexes on the targetCurrency
 * of each route. See ./README.md for more.
 * @param {*} response
 */
const buildTargetCurrencyToRouteMap = (response) => ({
  routes: response?.routes?.reduce(
    (accumulatorObject, route) => ({
      ...accumulatorObject,
      [route.targetCurrency]: {
        ...route,
      },
    }),
    {},
  ),
});

/**
 * Used when merging data structures. For a given route from the
 * baseProvider, check if the comparedProvider has the same route.
 * If yes, return the route data, otherwise say the route is
 * not supported.
 * @param {*} comparedProviderTargetCurrencyToRouteMap
 * @param {*} baseProviderRoute
 * @param {*} comparedProvider
 */
const getComparedProviderQuote = (
  comparedProviderTargetCurrencyToRouteMap,
  baseProviderRoute,
  comparedProvider,
) => {
  if (comparedProviderTargetCurrencyToRouteMap.routes[baseProviderRoute.targetCurrency]) {
    return {
      alias: comparedProvider,
      supportedRoute: true,
      ...comparedProviderTargetCurrencyToRouteMap.routes[baseProviderRoute.targetCurrency],
    };
  }
  // If comparedProviderTargetCurrencyToRouteMap is missing a route:
  return {
    supportedRoute: false,
  };
};

/**
 * Merges the data from 2 providers into one array, for use in ProviderComparisonTable
 * @param {*} baseProviderRaw
 * @param {*} comparedProviderRaw
 * @param {*} comparedProvider
 */
const convertApiDataToRoutes = (baseProviderRaw, comparedProviderRaw, comparedProvider) => {
  const baseProviderData = transformProviderResponse(baseProviderRaw);
  const comparedProviderData = transformProviderResponse(comparedProviderRaw);
  const comparedProviderTargetCurrencyToRouteMap =
    buildTargetCurrencyToRouteMap(comparedProviderData);
  return baseProviderData?.routes?.map((route) => ({
    baseProviderQuote: {
      ...route,
    },
    comparedProviderQuote: getComparedProviderQuote(
      comparedProviderTargetCurrencyToRouteMap,
      route,
      comparedProvider,
    ),
  }));
};

const generateInitialRoutes = (baseProviderData, comparedProviderData) => {
  if (!baseProviderData) {
    return [];
  }

  return convertApiDataToRoutes(baseProviderData, comparedProviderData);
};

const fetchRoutes = async (baseProvider, comparedProvider, source, sendAmount) => {
  try {
    const baseProviderRaw = await fetchProviderRoutes(baseProvider, source, sendAmount);

    const comparedProviderRaw = await fetchProviderRoutes(comparedProvider, source, sendAmount);

    return convertApiDataToRoutes(baseProviderRaw, comparedProviderRaw, comparedProvider);
  } catch (error) {
    // TODO: Log error
    throw new Error(error);
  }
};

// The goal of this component is to provide an API client for
// the UI widget. It should avoid managing state.
const withProviderComparisonResources = (Component) => {
  const Wrapped = ({
    name,
    alias,
    logo,
    initialBaseProviderData,
    initialComparedProviderData,
    initialSourceCurrency,
    initialTargetCurrency,
    initialSendAmount,
    trackEvent,
    trackLeavePageEvent,
  }) => {
    const initialRoutes = generateInitialRoutes(
      initialBaseProviderData,
      initialComparedProviderData,
    );

    return (
      // eslint-disable-next-line react/jsx-filename-extension
      <Component
        name={name}
        alias={alias}
        logo={logo}
        fetchRoutes={fetchRoutes}
        initialRoutes={initialRoutes}
        initialSourceCurrency={initialSourceCurrency}
        initialTargetCurrency={initialTargetCurrency}
        initialSendAmount={initialSendAmount}
        trackEvent={trackEvent}
        trackLeavePageEvent={trackLeavePageEvent}
      />
    );
  };

  Wrapped.propTypes = {
    name: PropTypes.string.isRequired,
    alias: PropTypes.string.isRequired,
    logo: PropTypes.string.isRequired,
    initialBaseProviderData: PropTypes.shape({}).isRequired,
    initialComparedProviderData: PropTypes.shape({}).isRequired,
    initialSourceCurrency: PropTypes.string.isRequired,
    initialTargetCurrency: PropTypes.string.isRequired,
    initialSendAmount: PropTypes.number.isRequired,
    trackEvent: PropTypes.func,
    trackLeavePageEvent: PropTypes.func,
  };

  return Wrapped;
};

export default withProviderComparisonResources;
